import {
    PageHeader,
    Button,
    ColourSolid, FlexAlign, FlexGap,
    FlexJustify, FullWidthSidebar,
    InfiniteLoadTrigger, ItemRowHeader,
    StackVertical,
    useLayout, useNotifications, RichEmptyState
} from "@twigsby/frontend-framework";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "@twigsby/frontend-framework";
import {EndpointError} from "../../Components/EndpointError";
import {MailBoxImage} from "../../Components/Images/MailboxImage";
import SendsNavigation from "../../Components/SendMail/Shared/Controls/SendsNavigation";
import SendsUserItem from "../../Components/SendsAdmin/SendsUserItem";
import CreateUserModal from "../../Components/SendsAdmin/Modals/CreateUserModal";
import ConfirmDeleteUserModal from "../../Components/SendsAdmin/Modals/ConfirmDeleteUserModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBomb} from "@fortawesome/pro-solid-svg-icons/faBomb";
import {faUser} from "@fortawesome/pro-solid-svg-icons/faUser";
import {faKey} from "@fortawesome/pro-solid-svg-icons/faKey";
import {faUsers} from "@fortawesome/pro-solid-svg-icons/faUsers";
import {
    disableUser,
    GetItemCounts,
    sendPasswordResetEmail,
    useInfiniteAccountUsers,
    UserDataType, UsersUpdateType,
    useUpdateAccountUsers
} from "@twigsby/client-data";
import {useSite} from "../../Providers/SiteInterfaceProvider";

const Users = () => {
    const { t } = useTranslation();
    const { addNotification } = useNotifications();
    const { isMedium, isLarge } = useLayout();
    const { currentLocale, updatePageMeta } = useSite();

    const {
        status,
        data,
        error,
        isSuccess,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } = useInfiniteAccountUsers();

    const [ createUserOpen , setCreateUserOpen ] = useState<boolean>(false);
    const [ activeUser, setActiveUser ] = useState<any>({ isOpen: false, user: null});
    const [ itemCounts, setItemCounts ] = useState<any>({ loaded: 0, total: 0 } );

    const updateUsers = useUpdateAccountUsers();

    const sendsUsersRef = useRef<any>();
    const sendMailNavigationRef = useRef<any>();

    const SendPasswordReset = (userId: number) => {
        sendPasswordResetEmail(userId).then(r => {
            if(r) {
                addNotification({
                    title: t('results.passwordResetSent'),
                    icon: faKey,
                    colour: ColourSolid.Success
                });
            } else {
                addNotification({
                    title: t('results.passwordResetFailed'),
                    icon: faKey,
                    colour: ColourSolid.Alert
                })
            }
        });
    }

    const ShowDeleteConfirmation = (user: UserDataType) => {
        setActiveUser({ user: user, isOpen: true })
    }

    const HandleDisable = async (userId: number, disabled: boolean) => {
        disableUser(userId, disabled).then((r) => {
            updateUsers.mutate({
                updateType: UsersUpdateType.DISABLE,
                newData: userId,
            })
        });
    }

    const ItemPlaceholder = () => {
        return (
            <div className={'content-loader content-fader'} style={{ display: 'grid', gap: 'var(--sm-unit)', margin: '0 var(--md-unit)', gridTemplateColumns: isMedium ? 'repeat(3, 1fr) 5em' : '1fr 5em'}}>
                {isMedium ? (
                    <>
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                    </>
                ) : (
                    <>
                        <div style={{ width: 'auto', height: '2em' }} />
                        <div style={{ width: 'auto', height: '2em' }} />
                    </>
                )}
            </div>
        )
    }

    const PageActions = () => {
        return (
            <Button
                colour={ColourSolid.Primary}
                onClick={() => setCreateUserOpen(true)}
                squared={true}
                title={t('sends.users.actions.add')}
                icon={faUser}
            />
        )
    }

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data as any));
        }
    }, [data]);

    useEffect(() => {
        updatePageMeta({
            title: t('sends.users.title')
        });
    }, [currentLocale]);

    return (
        <FullWidthSidebar
            ref={sendsUsersRef}
            navigationComponent={
                <>
                    <SendsNavigation
                        ref={sendMailNavigationRef}
                        title={t('sends.users.title')}
                        addonContent={
                            <div className={'section-navigation-header-collapsed-addon'}>
                                {isSuccess && !isLarge && (
                                    <PageActions />
                                )}
                            </div>
                        }
                    />
                </>
            }
            extraContentClass={`flex-column sends-users-rows`}
        >
            {isLarge && (
                <PageHeader title={t('sends.users.title')}>
                    {isSuccess && (
                        <PageActions />
                    )}
                </PageHeader>
            )}

            {isMedium && (
                <ItemRowHeader sticky={true} extraClass={'pl-lg'}>
                    <div>{t('sends.users.headings.name')}</div>
                    <div>{t('sends.users.headings.email')}</div>
                    {/* <div>{t('sends.users.headings.roles')}</div>*/}
                    <div>{t('sends.users.headings.actions')}</div>
                    <div>{t('sends.users.headings.active')}</div>
                </ItemRowHeader>
            )}

            {status === "loading" ? (
                <div className={'content-fader'}>
                    <ItemPlaceholder/>
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                </div>
            ) : status === "error" ? (
                <EndpointError {...error} stacked={true} extraClass={'m-md'} />
            ) : (
                <>
                    {data && data.pages.map((group) =>
                        group.data.map((entry) =>
                            <SendsUserItem
                                {...entry}
                                key={entry.userId}
                                disableCallback={(checked: boolean) => HandleDisable(entry.userId, checked)}
                                passwordResetCallback={() => SendPasswordReset(entry.userId)}
                                deleteCallback={() => ShowDeleteConfirmation(entry)}
                            />
                        )
                    )}

                    {itemCounts.total === 0 && (
                        <RichEmptyState
                            title={t("sends.users.noData.title")}
                            description={t("sends.users.noData.description")}
                            media={<FontAwesomeIcon icon={faUsers} size={"3x"} />}
                            addon={<Button title={t("sends.profiles.actions.getStarted")} colour={ColourSolid.Primary} onClick={() => setCreateUserOpen(true)} />}
                        />
                    )}

                    {itemCounts.total > 0 && (
                        <InfiniteLoadTrigger
                            loaded={itemCounts.loaded}
                            totalCount={itemCounts.total}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    )}
                </>
            )}

            <CreateUserModal isOpen={createUserOpen} closeCallback={() => setCreateUserOpen(false) } />

            <ConfirmDeleteUserModal
                user={activeUser.user}
                isOpen={activeUser.isOpen}
                closeCallback={() => setActiveUser({...activeUser, isOpen: false})}
            />

        </FullWidthSidebar>
    )
}

export default Users
